import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/services/api-class';
import type {
  FormInstanceData,
  FormInstanceUpsertResultData,
  FormSchemaData,
  UpsertFormInstanceCommandData,
} from '@/services/api-types';
import { extractServerErrors, type ServerError } from '@/components/forms/form-errors';

export interface UseFormInstanceOptions {
  /** The schema the caller wants to edit. `null` until picked. */
  schemaId: number | null;
  /** Host record type — e.g. 'CLIENT'. */
  recordType: string;
  /** Host record id. `null` until the caller has fetched the host. */
  recordId: number | null;
  /** Auto-fetch when `schemaId && recordId` are both set. Default `true`. */
  autoLoad?: boolean;
}

export interface UseFormInstanceResult {
  schema: FormSchemaData | null;
  instance: FormInstanceData | null;
  isLoading: boolean;
  error: string | null;
  /**
   * Structured per-error responses from the most recent failed save. Cleared
   * on next successful save and on `clearServerErrors()`. Used by the panel
   * to map per-field validation failures (`:fieldKey` suffix) back to the
   * offending input for inline highlighting.
   */
  serverErrors: ServerError[];
  clearServerErrors: () => void;
  save: (values: Record<string, unknown>) => Promise<FormInstanceUpsertResultData | null>;
  softDelete: () => Promise<boolean>;
  reload: () => void;
}

/**
 * Bundles schema + current instance(s) for a given host. Primary use case is
 * the Client screen where one instance per schema is expected — if multiple
 * exist we pick the first match and leave disambiguation to the caller.
 *
 * `save` wraps `api.upsertFormInstance` in a Promise. On create it sends the
 * schema id; on update the server reuses the existing instance's schema.
 * Resolves `null` and sets `error` if recordId or schemaId is missing.
 *
 * `softDelete` wraps `api.deleteFormInstance` — the backend performs a
 * soft delete (see implementation-plan §2.5).
 */
export function useFormInstance({
  schemaId,
  recordType,
  recordId,
  autoLoad = true,
}: UseFormInstanceOptions): UseFormInstanceResult {
  const [schema, setSchema] = useState<FormSchemaData | null>(null);
  const [instance, setInstance] = useState<FormInstanceData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<ServerError[]>([]);

  // Track the latest request's identity so stale responses don't overwrite newer state.
  const requestIdRef = useRef(0);

  const load = useCallback(() => {
    if (schemaId == null || recordId == null) {
      setSchema(null);
      setInstance(null);
      setError(null);
      return;
    }

    const reqId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    let schemaDone = false;
    let instancesDone = false;
    let loadedSchema: FormSchemaData | null = null;
    let loadedInstance: FormInstanceData | null = null;
    let sawError: string | null = null;

    const finish = () => {
      if (!schemaDone || !instancesDone) return;
      if (reqId !== requestIdRef.current) return; // stale
      setSchema(loadedSchema);
      setInstance(loadedInstance);
      setError(sawError);
      setIsLoading(false);
    };

    // Display-path schema fetch (module-scoped read endpoint) — viewing a form
    // must not require ROLE_FORM_MANAGEMENT. Always the current version.
    api.getFormSchemaForDisplay(
      schemaId,
      (result: FormSchemaData | null | undefined) => {
        loadedSchema = result ?? null;
        schemaDone = true;
        finish();
      },
      (err: any) => {
        sawError = err?.message ?? 'Failed to load form schema';
        schemaDone = true;
        finish();
      },
    );

    api.getFormInstances(
      recordType,
      recordId,
      (result: FormInstanceData[] | null | undefined) => {
        const list = Array.isArray(result) ? result : [];
        loadedInstance = list.find((it) => it.schemaId === schemaId) ?? null;
        instancesDone = true;
        finish();
      },
      (err: any) => {
        sawError = sawError ?? err?.message ?? 'Failed to load form instances';
        instancesDone = true;
        finish();
      },
    );
  }, [schemaId, recordType, recordId]);

  useEffect(() => {
    if (!autoLoad) return;
    load();
  }, [autoLoad, load]);

  const save = useCallback(
    (values: Record<string, unknown>): Promise<FormInstanceUpsertResultData | null> => {
      return new Promise((resolve) => {
        if (recordId == null || schemaId == null) {
          setError('Cannot save: schema or record not selected');
          resolve(null);
          return;
        }

        const cmd: UpsertFormInstanceCommandData = {
          id: instance?.id ?? null,
          // formSchemaId is only required on create; on update the server
          // reuses the existing instance's schema.
          formSchemaId: instance ? null : schemaId,
          recordType,
          recordId,
          values,
        };

        api.upsertFormInstance(
          cmd,
          (result: FormInstanceUpsertResultData | null | undefined) => {
            if (result?.instanceView) {
              setInstance(result.instanceView);
            }
            setError(null);
            setServerErrors([]);
            resolve(result ?? null);
          },
          (err: any) => {
            setError(err?.message ?? 'Failed to save form instance');
            setServerErrors(extractServerErrors(err));
            resolve(null);
          },
        );
      });
    },
    [instance, schema, schemaId, recordId, recordType],
  );

  const softDelete = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      if (instance?.id == null) {
        setError('Cannot delete: no form instance loaded');
        resolve(false);
        return;
      }
      api.deleteFormInstance(
        instance.id,
        () => {
          setInstance(null);
          setError(null);
          resolve(true);
        },
        (err: any) => {
          setError(err?.message ?? 'Failed to delete form instance');
          resolve(false);
        },
      );
    });
  }, [instance]);

  const clearServerErrors = useCallback(() => {
    setServerErrors([]);
  }, []);

  return {
    schema,
    instance,
    isLoading,
    error,
    serverErrors,
    clearServerErrors,
    save,
    softDelete,
    reload: load,
  };
}
